import { forwardRef, useCallback, useState } from 'react';
import FormInput from '../FormInput';
import type { ComponentPropsWithoutRef } from 'react';

// ============================================
// Utility Functions (Masks & Validations)
// ============================================

// CPF Mask: 000.000.000-00
export const maskCPF = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2');
};

// CNPJ Mask: 00.000.000/0000-00
export const maskCNPJ = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    return digits
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})/, '$1-$2');
};

// Phone Mask: (00) 00000-0000 ou (00) 0000-0000
export const maskPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 10) {
        return digits.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d{1,4})/, '$1-$2');
    }
    return digits.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d{1,4})/, '$1-$2');
};

// CEP/ZipCode Mask: 00000-000
export const maskZipCode = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    return digits.replace(/(\d{5})(\d{1,3})/, '$1-$2');
};

// Currency Mask: R$ 0.000,00
export const maskCurrency = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    const number = parseFloat(digits) / 100;
    return number.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    });
};

// Percentage Mask: 00,00%
export const maskPercentage = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 5);
    const number = parseFloat(digits) / 100;
    return number.toFixed(2).replace('.', ',') + '%';
};

// Remove all non-digit characters
export const unmask = (value: string): string => {
    return value.replace(/\D/g, '');
};

// ============================================
// Masked Input Components
// ============================================

interface MaskedInputProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'onChange' | 'onBlur'> {
    label?: string;
    i18nLabel?: any;
    onChange?: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (value: string, event: React.FocusEvent<HTMLInputElement>) => void;
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

// CPF Input
export const FormCPF = forwardRef<HTMLInputElement, MaskedInputProps>(({ onChange, onBlur, value = '', ...props }, ref) => {
    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const masked = maskCPF(e.target.value);
            e.target.value = masked;
            onChange?.(masked, e);
        },
        [onChange]
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            const unmasked = unmask(e.target.value);
            onBlur?.(unmasked, e);
        },
        [onBlur]
    );

    return <FormInput {...props} ref={ref} type="text" value={value} onChange={handleChange} onBlur={handleBlur} placeholder={props.placeholder || '000.000.000-00'} maxLength={14} />;
});
FormCPF.displayName = 'FormCPF';

// CNPJ Input
export const FormCNPJ = forwardRef<HTMLInputElement, MaskedInputProps>(({ onChange, onBlur, value = '', ...props }, ref) => {
    const [error, setError] = useState<string | null>(null);
    const maskedValue = maskCNPJ(value?.toString() ?? '');

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const masked = maskCNPJ(e.target.value);
            e.target.value = masked;
            onChange?.(masked, e);
            if (error) setError(null);
        },
        [onChange, error]
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            const unmasked = unmask(e.target.value);
            const isValid = unmasked.length === 0 || unmasked.length === 14;
            setError(isValid ? null : 'CNPJ inválido');
            onBlur?.(unmasked, e);
        },
        [onBlur]
    );

    return (
        <div className="space-y-1">
            <FormInput
                {...props}
                ref={ref}
                type="text"
                value={maskedValue}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={props.placeholder || '00.000.000/0000-00'}
                maxLength={18}
                data-cnpj="true"
                aria-invalid={error ? 'true' : undefined}
                className={`${props.className || ''} ${error ? 'border-danger focus:border-danger focus:ring-danger/30' : ''}`.trim()}
            />
            {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
    );
});
FormCNPJ.displayName = 'FormCNPJ';

// Phone Input
export const FormPhone = forwardRef<HTMLInputElement, MaskedInputProps>(({ onChange, onBlur, value = '', ...props }, ref) => {
    const [error, setError] = useState<string | null>(null);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const masked = maskPhone(e.target.value);
            e.target.value = masked;
            onChange?.(masked, e);
            if (error) setError(null);
        },
        [onChange, error]
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            const unmasked = unmask(e.target.value);
            const isValid = unmasked.length === 0 || unmasked.length === 10 || unmasked.length === 11;
            setError(isValid ? null : 'Telefone inválido');
            onBlur?.(unmasked, e);
        },
        [onBlur]
    );

    return (
        <div className="space-y-1">
            <FormInput
                {...props}
                ref={ref}
                type="tel"
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={props.placeholder || '(00) 00000-0000'}
                maxLength={15}
                aria-invalid={error ? 'true' : undefined}
                className={`${props.className || ''} ${error ? 'border-danger focus:border-danger focus:ring-danger/30' : ''}`.trim()}
            />
            {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
    );
});
FormPhone.displayName = 'FormPhone';

// ZipCode Input (CEP)
export const FormZipCode = forwardRef<HTMLInputElement, MaskedInputProps>(({ onChange, onBlur, value = '', ...props }, ref) => {
    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const masked = maskZipCode(e.target.value);
            e.target.value = masked;
            onChange?.(masked, e);
        },
        [onChange]
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            const unmasked = unmask(e.target.value);
            onBlur?.(unmasked, e);
        },
        [onBlur]
    );

    return <FormInput {...props} ref={ref} type="text" value={value} onChange={handleChange} onBlur={handleBlur} placeholder={props.placeholder || '00000-000'} maxLength={9} />;
});
FormZipCode.displayName = 'FormZipCode';

// Email Input
export const FormEmail = forwardRef<HTMLInputElement, MaskedInputProps>(({ onChange, onBlur, value = '', ...props }, ref) => {
    const [error, setError] = useState<string | null>(null);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onChange?.(e.target.value, e);
            if (error) setError(null);
        },
        [onChange, error],
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            const trimmed = e.target.value.trim();
            e.target.value = trimmed;
            const isValid = trimmed.length === 0 || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
            setError(isValid ? null : 'Email inválido');
            onBlur?.(trimmed, e);
        },
        [onBlur],
    );

    return (
        <div className="space-y-1">
            <FormInput
                {...props}
                ref={ref}
                type="email"
                inputMode="email"
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={props.placeholder || 'nome@dominio.com'}
                aria-invalid={error ? 'true' : undefined}
                className={`${props.className || ''} ${error ? 'border-danger focus:border-danger focus:ring-danger/30' : ''}`.trim()}
            />
            {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
    );
});
FormEmail.displayName = 'FormEmail';

// Currency Input
export const FormCurrency = forwardRef<HTMLInputElement, MaskedInputProps>(({ onChange, onBlur, value = '', ...props }, ref) => {
    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const masked = maskCurrency(e.target.value);
            e.target.value = masked;
            onChange?.(masked, e);
        },
        [onChange]
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            const unmasked = unmask(e.target.value);
            onBlur?.(unmasked, e);
        },
        [onBlur]
    );

    return <FormInput {...props} ref={ref} type="text" value={value} onChange={handleChange} onBlur={handleBlur} placeholder={props.placeholder || 'R$ 0,00'} />;
});
FormCurrency.displayName = 'FormCurrency';

// Percentage Input
export const FormPercentage = forwardRef<HTMLInputElement, MaskedInputProps>(({ onChange, onBlur, value = '', ...props }, ref) => {
    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const masked = maskPercentage(e.target.value);
            e.target.value = masked;
            onChange?.(masked, e);
        },
        [onChange]
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            const unmasked = unmask(e.target.value);
            onBlur?.(unmasked, e);
        },
        [onBlur]
    );

    return <FormInput {...props} ref={ref} type="text" value={value} onChange={handleChange} onBlur={handleBlur} placeholder={props.placeholder || '0,00%'} />;
});
FormPercentage.displayName = 'FormPercentage';

// Export all utilities
export const masks = {
    cpf: maskCPF,
    cnpj: maskCNPJ,
    phone: maskPhone,
    zipCode: maskZipCode,
    currency: maskCurrency,
    percentage: maskPercentage,
    unmask,
};

// No aliases: prefer Form*-prefixed components directly
